import {
  createNativeStackNavigator,
  NativeStackScreenProps,
} from '@react-navigation/native-stack';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { Txt } from 'components/Txt';
import { useAlert } from 'hooks/useAlert';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDispatch } from 'react-redux';
import { SyncIndicator } from 'screens/SyncScreen/SyncIndicator';
import { CLOSE_ASSESSMENT } from 'store/assessments/actions';
import {
  selectAseTplFieldGroup,
  selectAseFieldInstanceAseMerged,
  selectAseIsOpen,
  selectAseIsOnline,
} from 'store/assessments/selectors';
import { ActionRepeatableScreen } from './ActionScreen';
import { ControlRepeatableScreen } from './ControlScreen';
import { QuestionRepeatableScreen } from './QuestionScreen';
import { RepeatableInstanceScreenForNavigation } from './Repeatable/RepeatableInstanceScreen';
import { RepeatableListIntroScreen } from './Repeatable/RepeatableListIntroScreen';
import { RepeatableListScreen } from './Repeatable/RepeatableListScreen';
import { FieldInstanceCheckScreen } from './AseInstanceCheckScreen';

const Stack =
  createNativeStackNavigator<NavigationStacks[Stacks.ASEREPEATABLE]>();

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ROOT],
  Screens.ROOT_ASEREPEATABLE
>;

type ListProps = NativeStackScreenProps<
  NavigationStacks[Stacks.ASEREPEATABLE],
  Screens.ASEREPEATABLE_LIST
>;

export const RepeatableStackScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const screenSize = useScreenSize();
  const { top } = useSafeAreaInsets();
  const { groupId, parent } = route.params;

  const isOpen = useSelector(selectAseIsOpen);
  const isOnline = useSelector(selectAseIsOnline);

  const parentInstance = useSelector((state) =>
    selectAseFieldInstanceAseMerged(state, parent?.instanceId || '-1'),
  );
  const parentGroup = useSelector((state) =>
    selectAseTplFieldGroup(state, parentInstance?.groupId || '-1'),
  );

  const stack = React.useMemo(() => {
    return !isOpen ? null : (
      <Stack.Navigator
        initialRouteName={
          screenSize === 'small'
            ? Screens.ASEREPEATABLE_LIST
            : Screens.ASEREPEATABLE_INTRO
        }
        screenOptions={{
          headerShown: false,
          animation: screenSize === 'small' ? 'default' : 'none',
        }}
      >
        {screenSize === 'small' && (
          <Stack.Screen
            name={Screens.ASEREPEATABLE_LIST}
            component={RepeatableListScreen}
            initialParams={{ groupId, parent }}
          />
        )}
        <Stack.Screen
          name={Screens.ASEREPEATABLE_INTRO}
          component={RepeatableListIntroScreen}
          initialParams={{ groupId, parent }}
        />
        <Stack.Screen
          name={Screens.ASEREPEATABLE_INSTANCE}
          component={RepeatableInstanceScreenForNavigation}
          initialParams={{ groupId, parent }}
        />
        <Stack.Screen
          name={Screens.AseInstanceCheck}
          component={FieldInstanceCheckScreen}
        />
        <Stack.Screen
          name={Screens.ASEREPEATABLE_QUESTION}
          component={QuestionRepeatableScreen}
        />
        <Stack.Screen
          name={Screens.ASEREPEATABLE_ACTION}
          component={ActionRepeatableScreen}
        />
        <Stack.Screen
          name={Screens.ASEREPEATABLE_CONTROL}
          component={ControlRepeatableScreen}
        />
      </Stack.Navigator>
    );
  }, [isOpen, screenSize, groupId, parent]);

  const PageMemo = React.useMemo(
    () =>
      !isOpen ? null : (
        <ErrorBoundary
          onError={() => {
            alert({
              text: 'The assessment was closed because something went wrong. This has been logged and sent to RiskBase.',
            });
            dispatch(CLOSE_ASSESSMENT());
          }}
        >
          {screenSize === 'large' ? (
            <View style={{ flexDirection: 'row', flex: 1 }}>
              <View
                style={{
                  flex: 1,
                  borderColor: '#ddd',
                  borderRightWidth: 1,
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                }}
              >
                <RepeatableListScreen
                  navigation={navigation as unknown as ListProps['navigation']}
                  route={route as unknown as ListProps['route']}
                />
              </View>
              <View
                style={{
                  flex: 1,
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                }}
              >
                {stack}
              </View>
            </View>
          ) : (
            stack
          )}
          <SyncIndicator showSyncIcon={!isOnline} />
        </ErrorBoundary>
      ),
    [isOpen, screenSize, navigation, route, stack, isOnline, alert, dispatch],
  );

  React.useEffect(() => {
    // Navigate back if assessment is not open, or there is a parent instance but it's not found
    if (!isOpen || (parent?.instanceId && !parentInstance)) {
      navigation.goBack();
    }
  }, [isOpen, navigation, dispatch, parentInstance, parent?.instanceId]);

  return parentInstance && parentGroup ? (
    <Page
      header={
        <View
          style={{
            paddingTop: 6 + top,
            paddingBottom: 6,
            paddingHorizontal: theme.padding,
          }}
        >
          <View
            style={{
              paddingHorizontal: theme.padding,
              paddingVertical: theme.paddingHorizontalHalf,
              backgroundColor: theme.colors.green10,
              borderRadius: 48,
            }}
          >
            <Txt
              weight="bold"
              style={{
                textAlign: 'center',
                flex: 1,
              }}
              numberOfLines={1}
            >
              {parentGroup.calculatedInfo.singularName}:{' '}
              {parentInstance.formattedAutoId}
            </Txt>
          </View>
        </View>
      }
    >
      {PageMemo}
    </Page>
  ) : (
    PageMemo
  );
};
